import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Breadcrumbs from "../../components/Breadcrumbs";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import HardwareProductForm from "../../components/hardwareCatalogue/HardwareProductForm";
import { EMPTY_HARDWARE_PRODUCT, toHardwareProductPayload, validateImageFile } from "../../components/hardwareCatalogue/hardwareProductPayload";
import { useAuth } from "../../providers/AuthContext";
import { createHardwareProduct, uploadHardwareProductImage } from "../../api/HardwareProducts";

const CreateHardwareProductPage = () => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(EMPTY_HARDWARE_PRODUCT);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => {} });
  // The product has no id before it is saved, so a chosen image waits here and is uploaded right after.
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState("");

  useEffect(() => {
    if (!pendingImage) {
      setPendingImagePreview("");
      return undefined;
    }
    const url = URL.createObjectURL(pendingImage);
    setPendingImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImage]);

  const handleBackClick = () => {
    if (!hasChanges) return navigate(-1);
    setConfirmState({ isOpen: true, title: "Discard changes?", confirm: () => navigate(-1) });
  };

  const handleImageSelected = (file) => {
    const problem = validateImageFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setPendingImage(file);
    setHasChanges(true);
  };

  const handleSave = async () => {
    const payload = toHardwareProductPayload(product);
    if (!payload.name) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }
      const created = await createHardwareProduct(payload, accessToken);
      setHasChanges(false);

      if (pendingImage && created?.hardwareProductPublicId) {
        try {
          await uploadHardwareProductImage(created.hardwareProductPublicId, pendingImage, accessToken);
        } catch (error) {
          // The product exists; the image can be added from its edit page.
          toast.error(`Product created, but the image was not uploaded: ${error.message}`);
          navigate("/hardware-catalogue");
          return;
        }
      }

      toast.success("Product created", { iconTheme: { primary: "#4CAF50", secondary: "#DFF2BF" } });
      navigate("/hardware-catalogue");
    } catch (error) {
      toast.error(error.message || "Failed to create product");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"Create product"} loading={false} />
          </div>
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex items-center gap-4 mb-4">
              <button className="text-xl" onClick={handleBackClick} aria-label="Back">
                <FontAwesomeIcon icon={faArrowLeftLong} />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Create hardware product</h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary" onClick={handleBackClick}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSave} disabled={!hasChanges || isSaving}>Save</button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <HardwareProductForm
                product={product}
                setProduct={setProduct}
                setHasChanges={setHasChanges}
                onImageSelected={handleImageSelected}
                pendingImagePreview={pendingImagePreview}
                imageBusy={isSaving}
              />
            </div>
          </div>
        </main>
      </div>
      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default CreateHardwareProductPage;
